import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", firm_name: "", message: "" });
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in name, email, and message.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, {
        name: form.name,
        email: form.email,
        firm_name: form.firm_name || null,
        message: form.message,
      });
      toast.success("Thanks — we'll get back to you within one business day.");
      setForm({ name: "", email: "", firm_name: "", message: "" });
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Submission failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <section className="border-b border-border">
        <div className="container-x py-20 md:py-28 max-w-4xl">
          <p className="overline">Contact</p>
          <h1 className="font-serif text-5xl md:text-7xl tracking-tighter leading-[0.98] mt-5 font-light">
            Talk to <span className="italic text-[#002147]">our team.</span>
          </h1>
          <p className="mt-6 text-lg text-muted-foreground max-w-2xl">
            Whether you&apos;re a partner, GC, or legal-ops lead — tell us about your contract workflow.
            We respond within one business day.
          </p>
        </div>
      </section>

      <section className="container-x py-20 md:py-28 grid grid-cols-1 md:grid-cols-12 gap-12">
        <aside className="md:col-span-4 space-y-10">
          <div>
            <p className="overline">Email</p>
            <a className="font-serif text-2xl mt-2 block hover:underline" href="mailto:contact@cognita-ai.com">
              contact@cognita-ai.com
            </a>
          </div>
          <div>
            <p className="overline">For legal</p>
            <p className="text-sm text-muted-foreground mt-2">
              Cognita AI, Inc. <br />
              Delaware, United States
            </p>
          </div>
        </aside>

        <form onSubmit={submit} data-testid="contact-form" className="md:col-span-8 border border-border bg-white p-8 md:p-12">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <Field label="Name *" id="c-name" testid="contact-name-input" value={form.name} onChange={set("name")} required />
            <Field label="Work email *" id="c-email" testid="contact-email-input" type="email" value={form.email} onChange={set("email")} required />
          </div>
          <div className="mt-6">
            <Field label="Firm" id="c-firm" testid="contact-firm-input" value={form.firm_name} onChange={set("firm_name")} />
          </div>
          <div className="mt-6">
            <label htmlFor="c-msg" className="overline block mb-2">Message *</label>
            <textarea
              id="c-msg"
              data-testid="contact-message-input"
              required
              rows={6}
              value={form.message}
              onChange={set("message")}
              placeholder="Tell us about your team, contract volume, and what you're trying to solve."
              className="w-full border border-border bg-white px-4 py-3 text-sm focus:outline-none focus:ring-1 focus:ring-foreground focus:border-foreground resize-none"
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            data-testid="contact-submit-btn"
            className="mt-8 inline-flex items-center gap-2 px-7 py-3.5 bg-[#0A0A0A] text-white text-sm font-medium hover:bg-[#002147] transition-colors disabled:opacity-60"
          >
            {loading ? "Sending…" : (<>Send message <ArrowRight size={16} /></>)}
          </button>
        </form>
      </section>
    </div>
  );
}

function Field({ label, id, value, onChange, type = "text", required = false, testid }) {
  return (
    <div>
      <label htmlFor={id} className="overline block mb-2">{label}</label>
      <input
        id={id}
        data-testid={testid}
        type={type}
        required={required}
        value={value}
        onChange={onChange}
        className="w-full border border-border bg-white px-4 py-3 text-sm focus:outline-none focus:ring-1 focus:ring-foreground focus:border-foreground"
      />
    </div>
  );
}
